import type { AgentPreset, AgentProbe } from '@agentwiki/shared'

interface RunMcpConnectionTestOptions {
  endpoint: string
  apiKey: string
  preset: AgentPreset
}

export interface McpProbeResult {
  toolName: string
  ok: boolean
  message: string
}

export interface McpConnectionTestResult {
  ok: boolean
  initialized: boolean
  toolsListed: boolean
  toolCount: number
  allowedProbe: McpProbeResult
  deniedProbe: McpProbeResult
}

function sanitizeMessage(message: string, apiKey: string) {
  return apiKey ? message.replaceAll(apiKey, '<redacted>') : message
}

function resultText(result: unknown): string {
  if (typeof result !== 'object' || result === null) return ''
  const content = (result as { content?: unknown }).content
  if (!Array.isArray(content)) return ''
  return content
    .map((item) => {
      if (typeof item === 'object' && item !== null && 'text' in item) return String((item as { text: unknown }).text)
      return ''
    })
    .join('\n')
}

function isPermissionDenied(result: unknown) {
  const text = resultText(result)
  return /permission denied|scope escalation denied|required/i.test(text)
}

async function callMcp(endpoint: string, apiKey: string, method: string, params: unknown, id: number) {
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json, text/event-stream',
      Authorization: `Bearer ${apiKey}`,
      'Mcp-Protocol-Version': '2025-06-18',
    },
    body: JSON.stringify({ jsonrpc: '2.0', id, method, params }),
  })

  const text = await response.text()
  let data: unknown
  try {
    data = text ? JSON.parse(text) : null
  } catch {
    data = { error: { message: text } }
  }

  if (!response.ok) {
    const message =
      typeof data === 'object' && data !== null && 'error' in data
        ? String((data as { error?: { message?: unknown } }).error?.message ?? response.statusText)
        : response.statusText
    throw new Error(message)
  }

  return data as { result?: unknown; error?: { message?: string } }
}

async function callProbe(endpoint: string, apiKey: string, probe: AgentProbe, id: number): Promise<McpProbeResult> {
  const response = await callMcp(endpoint, apiKey, 'tools/call', {
    name: probe.toolName,
    arguments: probe.arguments,
  }, id)

  if (response.error) {
    return { toolName: probe.toolName, ok: false, message: response.error.message ?? 'MCP error' }
  }

  const permissionDenied = isPermissionDenied(response.result)
  return {
    toolName: probe.toolName,
    ok: !permissionDenied,
    message: resultText(response.result) || (permissionDenied ? `Missing ${probe.requiredScope}` : 'Probe completed'),
  }
}

export async function runMcpConnectionTest({
  endpoint,
  apiKey,
  preset,
}: RunMcpConnectionTestOptions): Promise<McpConnectionTestResult> {
  try {
    const initialized = await callMcp(endpoint, apiKey, 'initialize', {
      protocolVersion: '2025-06-18',
      capabilities: {},
      clientInfo: { name: 'agentwiki-web-onboarding', version: '1.0.0' },
    }, 1)
    if (initialized.error) throw new Error(initialized.error.message ?? 'Initialize failed')

    const tools = await callMcp(endpoint, apiKey, 'tools/list', {}, 2)
    if (tools.error) throw new Error(tools.error.message ?? 'tools/list failed')
    const toolList = typeof tools.result === 'object' && tools.result !== null && Array.isArray((tools.result as { tools?: unknown }).tools)
      ? (tools.result as { tools: unknown[] }).tools
      : []

    const allowedProbe = await callProbe(endpoint, apiKey, preset.allowedProbe, 3)
    const deniedRaw = await callProbe(endpoint, apiKey, preset.deniedProbe, 4)
    const deniedProbe = {
      ...deniedRaw,
      ok: !deniedRaw.ok,
      message: deniedRaw.ok ? `Expected ${preset.deniedProbe.requiredScope} to be blocked` : deniedRaw.message,
    }

    return {
      ok: allowedProbe.ok && deniedProbe.ok,
      initialized: true,
      toolsListed: true,
      toolCount: toolList.length,
      allowedProbe: {
        ...allowedProbe,
        message: sanitizeMessage(allowedProbe.message, apiKey),
      },
      deniedProbe: {
        ...deniedProbe,
        message: sanitizeMessage(deniedProbe.message, apiKey),
      },
    }
  } catch (error) {
    const message = sanitizeMessage(error instanceof Error ? error.message : 'MCP connection failed', apiKey)
    return {
      ok: false,
      initialized: false,
      toolsListed: false,
      toolCount: 0,
      allowedProbe: { toolName: preset.allowedProbe.toolName, ok: false, message },
      deniedProbe: { toolName: preset.deniedProbe.toolName, ok: false, message },
    }
  }
}
